/**
 * @fileoverview Automation module registry
 * @description This file contains the automation module registry and related functions
 * TODO: Write the reason for why this file is needed
 */

import * as React from 'react';
import type {GenericObject} from 'src/types/sense';
import {useApi} from 'src/hooks/useApi';
import {useApiQuery} from 'src/hooks/useApiQuery';

import {
  getActionMapEvents,
  getActionVariables,
  getAllVariables,
  processReqResVariables,
  getSupportedTypes,
  getVariableIdentifiers,
  skipFieldsRecursively,
} from './utils';

import isEmpty from 'lodash/isEmpty';


export type SimpleABTVariable = {
  title: string;
  show_on_ui: boolean;
  type: string;
  entity_type: string;
  key: string;
  description: string;
  possible_values: Array<boolean>;
  fieldPath?: string;
  fieldGroup?: string; //to identify if the variable is from abt or reqRes on previous node tab.;
  moduleKey: string;
  moduleName: string | null | undefined;
  prevNodeVariableType: 'abt';
  show_on_content: boolean;
  show_on_settings: boolean;
  show_on_logicalnodes: boolean;
  node_name?: string;
  display_name?: string;
};

export type SimpleVariable = {
  title: string;
  type: string;
  entity_type: string;
  relations?: Array<string>;
  entity_type_display_name: string;
  show_on_content: boolean;
  show_on_settings: boolean;
  show_on_logicalnodes: boolean;
  emsGroup: string;
  is_global_variable: boolean;
  show_on_ui: boolean;
  minimum?: number;
  maximum?: number;
};

export type ReqResVariable = {
  title: string;
  type: string;
  entity_type?: string;
  entity_type_display_name?: string;
  show_on_content: boolean;
  show_on_settings: boolean;
  show_on_logicalnodes: boolean;
  show_on_ui: boolean;
  moduleKey: string;
  moduleName: string | null | undefined;
  prevNodeVariableType: 'reqRes';
  node_name: string;
  display_name: string;
  nodeId: string;
  taskId: string;
  description: string;
  items_schema?: {
    type: string;
    enum?: Array<any>;
    [key: string]: any;
  };
  selectionLevelPath: string;
  selectionLevelPathId: string;
  selectionLevelTitle: string;
  selection_text: string;
  enum?: Array<string>;
  minimum?: number;
  maximum?: number;
};

export type ABTUtilVariables = Map<
  string,
  Map<string, Map<string, SimpleABTVariable>>
>;

export type ABTVariables = {
  [key: string]:
    | {
        pickerVariables: Map<string, SimpleABTVariable>;
        utilVariables: ABTUtilVariables;
      }
    | null
    | undefined;
};
export type ReqResVariables = Map<
  string,
  Map<string, Map<string, ReqResVariable>>
>;

export type AutomationVariables<T> = {
  ems: Map<string, T>;
  abt: ABTVariables;
  reqRes: ReqResVariables;
  allowedActionEvents?: {
    [key: string]: Map<string, Map<string, Map<string, SimpleABTVariable>>>;
  };
  allowedReqResVariables?: Map<string, Map<string, ReqResVariable>>;
};

export type AutomationModulesContext<T> = {
  automationVariables: AutomationVariables<T>;
  previousNodeInfo: DynamicResponse | null | undefined;
  getStaticAutomationModule: (arg1: string) => GenericObject;
  getJSONPathMapForTaskGroup: (
    arg1: string,
    arg2?: string | null | undefined,
  ) => Map<string, any>;
  dynamicResponseSchema:
    | {
        [key: string]: DynamicResponse;
      }
    | null
    | undefined;
  getPreviousNodeInfo: (arg1: string) => DynamicResponse | null | undefined;
  isLoading: boolean;
  getModuleVariables: (
    nodeId: string,
  ) => Map<string, string> | null | undefined;
};

// NOTE:(diwakersurya): there is no other way to define a context
// which supports generic type we need to use `any` which is a
// supertype of all in this case. see
// https://stackoverflow.com/questions/60725621/react-context-with-generics
// for more info

// @ts-expect-error - TS2322 - Type 'Context<{ automationVariables: { ems: Map<any, any>; abt: {}; reqRes: Map<any, any>; allowedActionEvents: {}; allowedReqResVariables: Map<any, any>; }; previousNodeInfo: {}; getStaticAutomationModule: () => {}; ... 4 more ...; getModuleVariables: () => Map<...>; }>' is not assignable to type 'Context<AutomationModulesContext<any>>'.
export const AutomationModulesRegistryContext: React.Context<
  AutomationModulesContext<any>
> = React.createContext({
  automationVariables: {
    ems: new Map(),
    abt: {},
    reqRes: new Map(),
    allowedActionEvents: {},
    allowedReqResVariables: new Map(),
  },
  previousNodeInfo: {},
  getStaticAutomationModule: () => ({}),
  getJSONPathMapForTaskGroup: () => {
    return new Map();
  },
  dynamicResponseSchema: {},
  getPreviousNodeInfo: () => {},
  isLoading: false,
  getModuleVariables: () => new Map(),
});

export const VariableListMapContext: React.Context<{
  ems: Map<string, SimpleVariable>;
  reqRes: Map<string, ReqResVariable>;
  isLoading: boolean;
} | null> = React.createContext<{
  ems: Map<string, SimpleVariable>;
  reqRes: Map<string, ReqResVariable>;
  isLoading: boolean;
} | null>(null);

export type AutomationModuleRegistryHookOutput<T> = {
  getModuleVariables: (
    nodeId: string,
  ) => Map<string, string> | null | undefined;
  isLoading: boolean;
  automationVariables: AutomationVariables<T>;
  getStaticAutomationModule: (arg1: string) => GenericObject;
  getJSONPathMapForTaskGroup: (
    arg1: string,
    arg2?: string | null | undefined,
  ) => Map<string, any>;
  getActionVariables: (arg1: string) => {
    [key: string]: Map<string, Map<string, Map<string, SimpleABTVariable>>>;
  };
  getPreviousNodeInfo: (arg1: string) => DynamicResponse | null | undefined;
  dynamicResponseSchema:
    | {
        [key: string]: DynamicResponse;
      }
    | null
    | undefined;
  getAllowedReqResVariables: (
    arg1: string,
  ) => Map<string, Map<string, ReqResVariable>>;
};

export type StaticResponseSchema = {
  entity_candidate_fetch: GenericObject;
  email_send: GenericObject;
  entity_group_fetch: GenericObject;
  chatbot_trigger: GenericObject;
  action_fetch: GenericObject;
};

export type DynamicTaskObjectDetails = {
  id: string;
  node_name: string;
  display_name: string;
  task_group_type?: string;
  previous_node_context:
    | {
        key: string;
      }
    | null
    | undefined;
  anchor_entity_type: string;
  trigger_entity_type: string;
};

export type DynamicTaskObjectModuleDetails = {
  type: string;
  params: GenericObject;
  display_name: string;
  response_schema_key: string;
};

export type DynamicTaskObjectModules = {
  [key: string]: DynamicTaskObjectModuleDetails;
};

export type DynamicTaskObject = {
  details: DynamicTaskObjectDetails;
  modules: DynamicTaskObjectModules;
};

export type DynamicResponse = {
  [key: string]: DynamicTaskObject;
};

export type AvailableModulesHookOutput = {
  dynamicResponse: DynamicResponse;
  isLoading: boolean;
};

/**
 * TODO: Write the reason for why this function is needed
 */

export function useAutomationModulesRegistry<T = SimpleVariable>({
  workflowId,
  staticFetchDeps,
  dynamicFetchDeps,
}: {
  workflowId: string | null | undefined;
  staticFetchDeps?: Array<unknown>;
  dynamicFetchDeps?: Array<unknown>;
}): AutomationModuleRegistryHookOutput<T> {
  const [initialLoadComplete, setInitialLoadComplete] = React.useState(false);

  const effectiveStaticFetchDeps = staticFetchDeps ?? [workflowId];
  const effectiveDynamicFetchDeps = dynamicFetchDeps ?? [workflowId];
  const defaultStaticResponseSchema = {
    entity_candidate_fetch: {},
    email_send: {},
    entity_group_fetch: {},
    chatbot_trigger: {},
    action_fetch: {},
  } as const;

  //TODO:(diwakersurya) Fix this
  const staticApiUrl = workflowId
    ? `automation-schema/task-response-schema?workflow_id=${workflowId}`
    : null;
  const {result: staticResponseSchema, isLoading: isLoadingStatic} =
    useApi<StaticResponseSchema>(staticApiUrl, {
      shouldFetch: Boolean(workflowId),
      deps: effectiveStaticFetchDeps,
      apiPath: '/bff/api/v1/',
      cacheTimeout: 3600_000,
      key: [workflowId, ...effectiveStaticFetchDeps].join(':'),
    });

  const dynamicApiObject = workflowId
    ? {
        pathname: `workflow/${workflowId}/available-modules`,
      }
    : null;

  const {data: dynamicTaskResponseSchemaObj, isLoading: isLoadingDynamicTask} =
    useApiQuery<{
      [key: string]: DynamicResponse;
    }>(
      `automation-schema/dynamic-task-response-schema?workflow_id=${workflowId}`,
      {
        queryOptions: {
          queryKey: [...effectiveDynamicFetchDeps],
          enabled: !!workflowId,
        },
        apiOptions: {
          options: {
            apiPath: '/bff/api/v1/',
          },
        },
      },
    );

  const {result: dynamicResponseSchemaObj, isLoading: isLoadingDynamic} =
    useApi<{
      [key: string]: DynamicResponse;
    }>(dynamicApiObject, {
      deps: effectiveDynamicFetchDeps,
      shouldFetch: !!workflowId,
      apiPath: '/bff/api/v1/',
      cacheTimeout: 20_000,
      key: [workflowId, ...effectiveDynamicFetchDeps].join(':'),
    });

  // Effect to track when both APIs have loaded
  React.useEffect(() => {
    if (
      staticResponseSchema &&
      dynamicResponseSchemaObj &&
      !isLoadingStatic &&
      !isLoadingDynamic &&
      initialLoadComplete === false
    ) {
      setInitialLoadComplete(true);
    }
  }, [
    staticResponseSchema,
    dynamicResponseSchemaObj,
    isLoadingStatic,
    isLoadingDynamic,
  ]);

  const emsVariablesAndAutomationEventsRegistry = React.useMemo(
    () =>
      getEmsVariablesAndAbtEvents<T>(
        staticResponseSchema || defaultStaticResponseSchema,
      ),
    [workflowId, isLoadingStatic],
  );

  const reqResVariablesRegistry = React.useMemo(
    () =>
      getReqResVariablesRegistry<T>(
        staticResponseSchema || defaultStaticResponseSchema,
        dynamicResponseSchemaObj,
        dynamicTaskResponseSchemaObj, // start sending DRS
      ),
    [
      dynamicResponseSchemaObj,
      staticResponseSchema,
      dynamicTaskResponseSchemaObj,
    ],
  );

  const moduleVariableMap = React.useMemo(() => {
    return getModuleVariableMap(dynamicResponseSchemaObj);
  }, [dynamicResponseSchemaObj]);

  const getModuleVariables = (nodeId: string) => {
    return moduleVariableMap.get(nodeId);
  };

  const getStaticAutomationModule = (identifier: string): GenericObject => {
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'StaticResponseSchema'.
    return staticResponseSchema ? staticResponseSchema[identifier] : {};
  };

  const getJSONPathMapForTaskGroup = (
    taskGroup: string,
    entityType?: string | null,
  ): Map<string, any> => {
    const staticIdentifier = entityType
      ? `${taskGroup}__${entityType}`
      : taskGroup;
    const matchedStaticObject = getStaticAutomationModule(staticIdentifier);
    if (isEmpty(matchedStaticObject)) {
      return new Map();
    }
    const dataWithPropertiesSkipped = skipFieldsRecursively(
      {
        [taskGroup]: matchedStaticObject,
      },
      'properties',
    );
    return getVariableIdentifiers(dataWithPropertiesSkipped);
  };
  // Note: for every nodeId, we get the currect node and its previous node as next level Key.
  const getPreviousNodeInfo = (nodeId: string) => {
    if (!dynamicResponseSchemaObj) {
      return null;
    }
    return dynamicResponseSchemaObj[nodeId] ?? null;
  };

  const getActionVariables = (nodeId: string) => {
    const dynamicResponseSchema = getPreviousNodeInfo(nodeId);
    const actionVariables = getPreviousNodeActionEventsMapToEntityType(
      emsVariablesAndAutomationEventsRegistry.abt,
      dynamicResponseSchema,
    );

    return actionVariables;
  };

  const getAllowedReqResVariables = (prevNodeId: string) => {
    const result = reqResVariablesRegistry.get(prevNodeId);
    if (!result) {
      return new Map();
    }
    return result;
  };

  return {
    getModuleVariables,
    automationVariables: {
      ...emsVariablesAndAutomationEventsRegistry,
      reqRes: reqResVariablesRegistry,
    },
    isLoading: !initialLoadComplete,
    getPreviousNodeInfo,
    getActionVariables,
    getStaticAutomationModule,
    getJSONPathMapForTaskGroup,
    dynamicResponseSchema: dynamicResponseSchemaObj,
    getAllowedReqResVariables,
  };
}

/**
 * @param staticDataContext - this the response of /automation-schema/task-response-schema?workflow_id={workflowId}
 * @returns ems and abt events
 */
export function getEmsVariablesAndAbtEvents<T>(staticDataContext: unknown): {
  ems: Map<string, T>;
  abt: ABTVariables;
} {
  const actionFetchKeys = (
    staticDataContext != null && typeof staticDataContext === 'object'
      ? Object.keys(staticDataContext)
      : []
  ).filter((key) => key.startsWith('action_fetch')); // like: action_fetch__bh_candidate

  // Foreach entity type, we have a separate action_fetch field
  // we need to consider all of them
  const abtEvents = actionFetchKeys.reduce<ABTVariables>(
    // @ts-expect-error - TS2345 - Argument of type '(acc: ABTVariables, actionFetchKey: string) => { [x: string]: ABTVariables | { pickerVariables: Map<string, SimpleABTVariable>; utilVariables: ABTUtilVariables; } | null | undefined; }' is not assignable to parameter of type '(previousValue: ABTVariables, currentValue: string, currentIndex: number, array: string[]) => ABTVariables'.
    (acc, actionFetchKey) => {
      const afForEntityType = getActionVariables(
        typeof staticDataContext === 'object' &&
          staticDataContext != null &&
          // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
          staticDataContext[actionFetchKey]
          ? // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
            {[actionFetchKey]: staticDataContext[actionFetchKey]}
          : {},
      );
      const actionGroupId = getActionGroupFromId(actionFetchKey);
      if (actionGroupId) {
        return {...acc, [actionGroupId]: afForEntityType};
      }
      return acc;
    },
    {},
  );

  //Foreach entity type, we have a separate entity_group_fetch field
  // we need to consider all of them

  const entityGroupFetchKeys = (
    staticDataContext != null && typeof staticDataContext === 'object'
      ? Object.keys(staticDataContext)
      : []
  ).filter((key) => key.startsWith('entity_group_fetch'));

  //NOTE:(diwakersurya) should we memoise this?
  const ems = entityGroupFetchKeys.reduce((acc, entityGroupFetchKey) => {
    const egfForEntityType = getAllVariables<T>(
      typeof staticDataContext === 'object' &&
        staticDataContext != null &&
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
        staticDataContext[entityGroupFetchKey]
        ? // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
          {[entityGroupFetchKey]: staticDataContext[entityGroupFetchKey]}
        : {},
    );
    //map on each item and attach emsGroup to the value
    const egfForEntityTypeWithEmsGroup = new Map(
      [...egfForEntityType].map(([id, value]: [any, any]) => {
        return [
          id,
          {...value, emsGroup: getEmsGroupFromId(entityGroupFetchKey)},
        ];
      }),
    );

    return new Map([...acc, ...egfForEntityTypeWithEmsGroup]);
  }, new Map());

  return {
    ems,
    // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'ABTVariables'.
    abt: abtEvents,
  };
}

export function getModuleVariableMap(
  dynamicDataContext?: {
    [key: string]: DynamicResponse;
  } | null,
): Map<string, Map<string, string>> {
  const result = new Map<string, Map<string, string>>();
  if (!dynamicDataContext) {
    return result;
  }

  for (const taskKey of Object.keys(dynamicDataContext)) {
    const availableModulesObject = dynamicDataContext[taskKey];
    if (!availableModulesObject) {
      continue;
    }
    const taskData = availableModulesObject[taskKey];

    if (!taskData) {
      continue;
    }

    const innerMap = new Map<string, string>();

    // Process each available modules in the node
    for (const moduleKey of Object.keys(taskData.modules)) {
      const moduleData = taskData.modules[moduleKey];

      // Check if module type is chatbot_trigger or survey_trigger
      if (moduleData.type === 'chatbot_trigger') {
        innerMap.set(
          `$.${moduleKey}.response.long_url`,
          moduleData.display_name,
        );
      } else if (moduleData.type === 'survey_trigger') {
        innerMap.set(`$.${moduleKey}.response.url`, moduleData.display_name);
      } else if (moduleData.type === 'meetings_trigger') {
        innerMap.set(
          `$.${moduleKey}.response.link_url`,
          moduleData.display_name,
        );
      }
    }
    result.set(taskKey, innerMap);
  }
  return result;
}

export function getReqResVariablesRegistry<T>(
  staticDataContext: {
    [key: string]: GenericObject; // this is task-response-schema response
  },
  availableModulesDataContext?: {
    [key: string]: DynamicResponse;
  } | null, // this is available-modules response
  dynamicTaskResponseContext?: {
    [key: string]: GenericObject;
  } | null,
  // TODO: make dynamicTaskResponseContext 2nd param once we know all the use cases of getReqResVariablesRegistry
): ReqResVariables {
  //prepare all req res variables
  const reqResVariables = !availableModulesDataContext
    ? new Map()
    : Object.keys(availableModulesDataContext).reduce((acc, nodeId) => {
        const availableModules = availableModulesDataContext[nodeId];

        const reqResVariablesForNode = Object.keys(
          availableModules ?? {},
        ).reduce((accb, taskId) => {
          const taskObject = availableModules[taskId];
          const {details, modules} = taskObject;
          //remove the $ from the key because it will be added in the key generation process.
          const key = details.previous_node_context?.key;
          const node_name = details.node_name;
          const display_name = details.display_name;

          if (!key) {
            return accb;
          }
          // iterate over each module of the task and create the req res variables
          const reqResVariablesForTask = Object.keys(modules).reduce(
            (innerAcc, moduleId) => {
              const moduleObject = modules[moduleId];
              // we are introducing a new key response_schema_key in the module object
              const {
                type,
                display_name: moduleName,
                response_schema_key,
              } = moduleObject;
              // earlier type was used to lookup in the Task-Response-Schema
              // now we are using response_schema_key to lookup in the Dynamic-Task-Response-Schema
              const reqResSchemaObject =
                staticDataContext[response_schema_key] ||
                dynamicTaskResponseContext?.[response_schema_key];

              if (reqResSchemaObject) {
                const objectToCreateIdentifiers = {
                  [moduleId]: reqResSchemaObject,
                } as const;

                const variableIdentifiers = [
                  ...processReqResVariables(objectToCreateIdentifiers),
                ].reduce((vidAcc, [id, value]: [any, any]) => {
                  const identifier = createReqResIdentifier(key, id); // creating the identifier by prefixing key to path of Task Response Schema.
                  const selectionLevelPathId = createReqResIdentifier(
                    key,
                    value.selectionLevelPath,
                  ); // updating the identifier by prefixing key to selectionLevelPath of Task Response Schema.
                  return new Map([
                    ...vidAcc,
                    [
                      identifier,
                      {
                        ...value, // spreading values that come drs/tsr and add few more fields to the object.
                        selectionLevelPathId, // TODO need to rename this to selectionLevelId
                        moduleKey: moduleId,
                        node_name,
                        moduleName,
                        display_name,
                        taskId,
                        nodeId,
                      },
                    ],
                  ]);
                }, new Map());

                const mergedVariableIdentifiersForTaskId = new Map([
                  ...(innerAcc.get(taskId) ?? new Map()),
                  ...variableIdentifiers,
                ]);
                return new Map([
                  ...innerAcc,
                  [taskId, mergedVariableIdentifiersForTaskId],
                ]);
              }

              return innerAcc;
            },
            new Map(),
          );

          return new Map([...accb, ...reqResVariablesForTask]);
        }, new Map());

        return new Map([...acc, [nodeId, reqResVariablesForNode]]);
      }, new Map());
  return reqResVariables;
}

function getPreviousNodeActionEventsMapToEntityType(
  abt: ABTVariables,
  dynamicDataContext?: DynamicResponse | null,
): {
  [key: string]: Map<string, Map<string, Map<string, SimpleABTVariable>>>;
} {
  return Object.keys(abt).reduce<{
    [key: string]: Map<string, Map<string, Map<string, SimpleABTVariable>>>;
  }>((acc, afEntityType) => {
    const prvNodeActionEvents = getPreviousNodeActionEvents(
      abt[afEntityType]?.pickerVariables ?? new Map(),
      dynamicDataContext,
      afEntityType,
    );
    return {...acc, [afEntityType]: prvNodeActionEvents};
  }, {});
}
function getPreviousNodeActionEvents(
  abtEvents: Map<string, SimpleABTVariable>,
  dynamicDataContext: DynamicResponse | null | undefined,
  afEntityType: string,
): Map<string, Map<string, Map<string, SimpleABTVariable>>> {
  const taskActionMap = new Map();
  if (!dynamicDataContext) {
    return taskActionMap;
  }
  for (const taskKey of Object.keys(dynamicDataContext)) {
    const supportedTypes = getSupportedTypes(
      dynamicDataContext[taskKey].modules,
    );

    const actionMap =
      dynamicDataContext[taskKey].details.anchor_entity_type === afEntityType
        ? getActionMapEvents(supportedTypes, abtEvents)
        : new Map();
    taskActionMap.set(taskKey, actionMap);
  }

  return taskActionMap;
}

/** NOTE:(diwakersurya) backend provides entity group for each entity type
 * in format  entity_group_fetch__entityType
 * we extract the entityType from this key and return it
 * e.g. key entity_group_fetch__bh_candidate leads to entityType 'bh_candidate'

 */

const getEmsGroupFromId = (key: string) => {
  const match = key.match(/entity_group_fetch__(\w+)/);
  return match ? match[1] : null;
};

const getActionGroupFromId = (key: string) => {
  const match = key.match(/action_fetch__(\w+)/);
  return match ? match[1] : null;
};

/**
 * TODO: Write the reasone for why this function is needed
 * @param workflowId
 * @param previousNodeIdForActiveNode
 * @returns {Object}
 */

export function useAutomationModulesRegistrySetup(
  workflowId: string,
  previousNodeIdForActiveNode?: string | null,
): {
  getModuleVariables: (
    nodeId: string,
  ) => Map<string, string> | null | undefined;
  automationModulesRegistryLoading: boolean;
  automationVariables: AutomationVariables<SimpleVariable>;
  previousNodeInfo: DynamicResponse | null | undefined;
  getStaticAutomationModule: (moduleName: string) => GenericObject;
  dynamicResponseSchema:
    | {
        [key: string]: DynamicResponse;
      }
    | null
    | undefined;
  getJSONPathMapForTaskGroup: (
    taskGroup: string,
    entityType?: string | null | undefined,
  ) => Map<string, any>;
  refetchStatic: () => void;
  refetchDynamic: () => void;
  getPreviousNodeInfo: (nodeId: string) => DynamicResponse | null | undefined;
} {
  const [dynamicRefetchCount, setDynamicRefetchCount] = React.useState(0);
  const [staticRefetchCount, setStaticRefetchCount] = React.useState(0);
  const refetchDynamic = React.useCallback(
    () => setDynamicRefetchCount((prev) => prev + 1),
    [],
  );
  const refetchStatic = React.useCallback(
    () => setStaticRefetchCount((prev) => prev + 1),
    [],
  );
  const {
    getModuleVariables,
    automationVariables,
    isLoading: automationModulesRegistryLoading,
    getStaticAutomationModule,
    getActionVariables,
    getPreviousNodeInfo,
    dynamicResponseSchema,
    getJSONPathMapForTaskGroup,
    getAllowedReqResVariables,
  } = useAutomationModulesRegistry({
    workflowId,
    dynamicFetchDeps: [dynamicRefetchCount],
    staticFetchDeps: [staticRefetchCount],
  });
  const actionVariables = previousNodeIdForActiveNode
    ? getActionVariables(previousNodeIdForActiveNode)
    : {};
  const previousNodeInfo = previousNodeIdForActiveNode
    ? getPreviousNodeInfo(previousNodeIdForActiveNode)
    : null;
  //NOTE:(diwakersurya) these are set of variables which are valid on current node
  // it is used in automation variable picker
  const allowedReqResVariables = previousNodeIdForActiveNode
    ? getAllowedReqResVariables(previousNodeIdForActiveNode)
    : new Map();

  return {
    getModuleVariables,
    automationModulesRegistryLoading,
    automationVariables: {
      ...automationVariables,
      allowedActionEvents: actionVariables,
      allowedReqResVariables,
    },
    getStaticAutomationModule,
    previousNodeInfo,
    dynamicResponseSchema,
    refetchStatic,
    refetchDynamic,
    getJSONPathMapForTaskGroup,
    getPreviousNodeInfo,
  };
}

function createReqResIdentifier(key: string, id: string) {
  //NOTE:(diwakersurya) create complete identifier keys for reqRes variables
  //also remove $. form generated variables in order to create the complete key;
  return `{{${key}.${id.replace('{{$.', '')}`;
}
